// src/components/Login.js
import React, { useState } from 'react';
// Add useLocation to the import
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errorMessage, setErrorMessage] = useState('');

  // Add this line near the top of the component, next to useNavigate
const navigate = useNavigate();
const location = useLocation();  // ADD THIS

  const yellowGradient = 'linear-gradient(135deg, #F2C231 0%, #C69A32 100%)';

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post('https://api.cafpraticheonline.it/api/auth/login', {
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        const cookieConsent = localStorage.getItem('cookieConsent');

        if (cookieConsent === 'true') {
          // ✅ User accepted cookies — safe to store persistently
          localStorage.setItem('jwtToken', response.data.jwtToken);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } else {
          // ⚠️ User declined cookies — only keep temporarily
          sessionStorage.setItem('jwtToken', response.data.jwtToken);
          sessionStorage.setItem('user', JSON.stringify(response.data.user));

          alert('Hai rifiutato i cookie — la tua sessione terminerà quando chiuderai il browser.'

          );
        }

        navigate(location.state?.from || '/', { replace: true });
      } else {
        setErrorMessage(response.data.message || 'Accesso non riuscito. Per favore riprova.');
      }

    } catch (error) {
      console.error("Login error:", error.response ? error.response.data : error.message);
      setErrorMessage(error.response?.data?.message || 'Si è verificato un errore inatteso. Per favore riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ background: yellowGradient }}
          >
            <User className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Bentornato</h2>
          <p className="text-gray-600">Accedi al tuo account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Email"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="flex items-center text-red-600 text-sm mt-2">
              <AlertCircle className="h-4 w-4 mr-2" />
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Ricordami
              </label>
            </div>
            <div className="text-sm">
              {/* THIS IS THE CORRECT LINE */}
              <Link
                to="/forgot-password"
                className="font-medium text-yellow-600 hover:text-yellow-500"
              >
                Hai dimenticato la password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            style={{ background: yellowGradient }}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              'Accedi'
            )}
          </button>

          <div className="text-center">
            <p className="text-gray-600">
              Non hai un account?{' '}
              <Link
                to="/signup"
                className="font-medium text-yellow-600 hover:text-yellow-500 transition-colors"
              >
                Registrati ora
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;